import { Head, Link, router } from "@inertiajs/react"
import { Search } from "lucide-react"
import { useState } from "react"

import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import AdminLayout from "@/layouts/admin-layout"
import type { AdminUsersIndex } from "@/types/serializers"

type Props = AdminUsersIndex

export default function AdminUsersIndex({ users, pagination, filters }: Props) {
  const [search, setSearch] = useState(filters.search ?? "")
  const [role, setRole] = useState(filters.role ?? "")

  function handleSearch() {
    router.get("/admin/users", { search, role }, { preserveState: true })
  }

  function handleRoleFilter(value: string) {
    const newRole = value === "all" ? "" : value
    setRole(newRole)
    router.get(
      "/admin/users",
      { search, role: newRole },
      { preserveState: true },
    )
  }

  return (
    <AdminLayout>
      <Head title="Usuarios | Admin KrownFT" />

      <div className="space-y-6">
        <h1 className="text-foreground text-2xl font-bold">Usuarios</h1>

        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
            <Input
              placeholder="Buscar por nombre o email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              className="pl-10"
            />
          </div>
          <Select value={role || "all"} onValueChange={handleRoleFilter}>
            <SelectTrigger className="w-full sm:w-40">
              <SelectValue placeholder="Filtrar por rol" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="player">Jugador</SelectItem>
              <SelectItem value="scout">Scout</SelectItem>
              <SelectItem value="club">Club</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="border-border rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Rol</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Registrado</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <div>
                      <p className="text-foreground font-medium">
                        {user.full_name}
                      </p>
                      <p className="text-muted-foreground text-xs">
                        @{user.nickname}
                      </p>
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {user.email}
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">{user.role}</Badge>
                  </TableCell>
                  <TableCell>
                    {user.suspended_at ? (
                      <Badge variant="destructive">Suspendido</Badge>
                    ) : (
                      <Badge
                        variant="outline"
                        className="border-green-400/30 text-green-400"
                      >
                        Activo
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {new Date(user.created_at).toLocaleDateString("es-AR")}
                  </TableCell>
                  <TableCell className="text-right">
                    <Link
                      href={`/admin/users/${user.id}`}
                      className="text-krown-300 text-sm font-medium hover:underline"
                    >
                      Ver
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {pagination.pages > 1 && (
          <div className="flex items-center justify-center gap-2">
            {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(
              (page) => (
                <button
                  key={page}
                  onClick={() =>
                    router.get(
                      "/admin/users",
                      { search, role, page },
                      { preserveState: true },
                    )
                  }
                  className={`rounded-lg px-3 py-1 text-sm ${
                    page === pagination.page
                      ? "bg-krown-500 text-white"
                      : "text-muted-foreground hover:bg-secondary"
                  }`}
                >
                  {page}
                </button>
              ),
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  )
}
